import { cn } from "@sagot/ui";
import { LOGO_FULL_MIN_TILE, LOGO_PATH_FULL, LOGO_PATH_SIMPLE, LOGO_VIEW_BOX } from "./sagot-logo-paths";

interface BrandIconProps {
  className?: string;
}

/** Google "G" (the mockup's #i-google). Decorative: the button's text names the provider. */
export function GoogleMark({ className }: BrandIconProps) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" className={cn("size-[18px] shrink-0", className)}>
      <path d="M21 12.2c0-.7-.1-1.3-.2-1.9H12v3.6h5.1a4.4 4.4 0 0 1-1.9 2.9v2.4h3.1c1.7-1.7 2.7-4.1 2.7-7Z" fill="#4285F4" />
      <path d="M12 21.5c2.6 0 4.7-.9 6.3-2.3l-3.1-2.4c-.9.6-2 .9-3.2.9-2.5 0-4.6-1.7-5.3-3.9H3.5v2.5A9.5 9.5 0 0 0 12 21.5Z" fill="#34A853" />
      <path d="M6.7 13.8a5.7 5.7 0 0 1 0-3.6V7.7H3.5a9.5 9.5 0 0 0 0 8.6l3.2-2.5Z" fill="#FBBC05" />
      <path d="M12 6.3c1.4 0 2.6.5 3.6 1.4l2.7-2.7A9.2 9.2 0 0 0 12 2.5a9.5 9.5 0 0 0-8.5 5.2l3.2 2.5C7.4 8 9.5 6.3 12 6.3Z" fill="#EA4335" />
    </svg>
  );
}

/** Microsoft four squares (the mockup's #i-ms). Decorative. */
export function MicrosoftMark({ className }: BrandIconProps) {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false" className={cn("size-[18px] shrink-0", className)}>
      <rect x="2" y="2" width="7.5" height="7.5" fill="#F25022" />
      <rect x="10.5" y="2" width="7.5" height="7.5" fill="#7FBA00" />
      <rect x="2" y="10.5" width="7.5" height="7.5" fill="#00A4EF" />
      <rect x="10.5" y="10.5" width="7.5" height="7.5" fill="#FFB900" />
    </svg>
  );
}

interface SagotLogoMarkProps extends BrandIconProps {
  /**
   * Side of the tile that holds the mark, in px (default 28, the header tile). From 24 px up the full mark is drawn, with the CRUD.IT
   * glyph as a knockout; below it the simple mark, whose badge is a solid disc (the sprite's `i-logo` / `i-logo-simple`).
   * The svg itself is sized by CSS (0.78 x the tile side).
   */
  tile?: number;
}

/**
 * The black-and-white Sagot mark (branding/logo/sagot-bw-symbol.svg), drawn inline: one fill-only path in `currentColor`, so the tile's
 * CSS colour paints it (white on the black tile, black on the white tile). No ids, so it can be rendered any number of times on a page.
 * Decorative: the link around the lockup carries the name.
 */
export function SagotLogoMark({ className, tile = 28 }: SagotLogoMarkProps) {
  const full = tile >= LOGO_FULL_MIN_TILE;
  return (
    <svg viewBox={LOGO_VIEW_BOX} aria-hidden="true" focusable="false" className={className} data-logo-mark={full ? "full" : "simple"}>
      <path d={full ? LOGO_PATH_FULL : LOGO_PATH_SIMPLE} fill="currentColor" fillRule="evenodd" />
    </svg>
  );
}
